import { Spin } from 'antd';

import { CalendarTimeline } from '@pages/Calendar/ui/CalendarTimeline';
import { EmployeeBreakActionModal } from '@pages/Calendar/ui/EmployeeHeaderActions';
import { EmptyCalendarState } from '@pages/Calendar/ui/EmptyCalendarState';
import { DaysHeadersStrip } from '@pages/IndividualCalendar/ui/DaysHeadersStrip';
import { DaysViewport } from '@pages/IndividualCalendar/ui/DaysViewport';

import { useIndividualCalendarLayout } from '../hooks';

import type { CSSProperties, FC } from 'react';

import '../styles/IndividualCalendarLayout.scss';

export interface IndividualCalendarLayoutProps {}

type IndividualCalendarLayoutStyle = CSSProperties &
  Record<'--calendar-interval-height' | '--calendar-grid-step-height', string>;

export const IndividualCalendarLayout: FC<IndividualCalendarLayoutProps> = (
  props
) => {
  void props;

  const {
    todayMoment,
    visibleDays,
    centerRef,
    days,
    executorNonWorkingDates,
    dayAccessByDate,
    dayColumns,
    isLoading,
    intervalHeightPx,
    gridStepHeightPx,
    startTime,
    endTime,
    boundaryEndTime,
    bookingStepMinutes,
    timeFormat,
    goToPrevPeriodHandler,
    goToNextPeriodHandler,
    selectedBreak,
    slotClickHandler,
    slotBreakClickHandler,
    breakClickHandler,
    closeBreakModalHandler,
    breakModalSuccessHandler,
    onEventUpdate,
    onRecordClick,
    canShowContacts,
    previewFields,
    slotMenu,
    openSlotMenuHandler,
    closeSlotMenuHandler,
  } = useIndividualCalendarLayout();

  const layoutStyle: IndividualCalendarLayoutStyle = {
    '--calendar-interval-height': `${intervalHeightPx}px`,
    '--calendar-grid-step-height': `${gridStepHeightPx}px`,
  };

  if (isLoading) {
    return (
      <div
        className="IndividualCalendarLayout"
        data-testid="individual-calendar-layout"
      >
        <div className="IndividualCalendarLayout__empty">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div
        className="IndividualCalendarLayout"
        data-testid="individual-calendar-layout"
      >
        <div className="IndividualCalendarLayout__empty">
          <EmptyCalendarState />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="IndividualCalendarLayout"
        style={layoutStyle}
        data-testid="individual-calendar-layout"
      >
        <div className="IndividualCalendarLayout__scroll-area">
          <div className="IndividualCalendarLayout__timeline IndividualCalendarLayout__timeline--left">
            <CalendarTimeline
              side="left"
              startTime={startTime}
              endTime={endTime}
              boundaryEndTime={boundaryEndTime}
              stepMinutes={bookingStepMinutes}
              timeFormat={timeFormat}
              onNavigate={goToPrevPeriodHandler}
              navigationAriaLabel="Переключить на предыдущий период"
            />
          </div>

          <div className="IndividualCalendarLayout__center" ref={centerRef}>
            <DaysHeadersStrip
              days={visibleDays}
              todayMoment={todayMoment}
              executorNonWorkingDates={executorNonWorkingDates}
            />
            <div
              className="IndividualCalendarLayout__content"
              data-testid="individual-calendar-layout-content"
            >
              <DaysViewport
                days={days}
                dayAccessByDate={dayAccessByDate}
                dayColumns={dayColumns}
                startTime={startTime}
                endTime={endTime}
                stepMinutes={bookingStepMinutes}
                timeFormat={timeFormat}
                intervalHeightPx={intervalHeightPx}
                onSlotClick={slotClickHandler}
                onSlotBreakClick={slotBreakClickHandler}
                onBreakClick={breakClickHandler}
                onEventUpdate={onEventUpdate}
                onRecordClick={onRecordClick}
                canShowContacts={canShowContacts}
                previewFields={previewFields}
                activeSlotMenu={slotMenu}
                onSlotMenuOpen={openSlotMenuHandler}
                onSlotMenuClose={closeSlotMenuHandler}
              />
            </div>
          </div>

          <div className="IndividualCalendarLayout__timeline IndividualCalendarLayout__timeline--right">
            <CalendarTimeline
              side="right"
              startTime={startTime}
              endTime={endTime}
              boundaryEndTime={boundaryEndTime}
              stepMinutes={bookingStepMinutes}
              timeFormat={timeFormat}
              onNavigate={goToNextPeriodHandler}
              navigationAriaLabel="Переключить на следующий период"
            />
          </div>
        </div>
      </div>
      {selectedBreak && (
        <EmployeeBreakActionModal
          breakId={selectedBreak.id ?? undefined}
          date={selectedBreak.date}
          employeeId={selectedBreak.employeeId}
          employeeName={selectedBreak.employeeName}
          initialTimeS={selectedBreak.timeS}
          initialTimeE={selectedBreak.timeE}
          isOpen
          mode={selectedBreak.id === null ? 'create' : 'edit'}
          onClose={closeBreakModalHandler}
          onSuccess={breakModalSuccessHandler}
        />
      )}
    </>
  );
};
